import { useCallback, useEffect, useMemo, useState } from "react";
import { DataRow, DataTable, ProfileTabs, useToast } from "@breakingthelines/design-system";
import { useSearchParams } from "react-router-dom";

import { LoadingSpinner } from "@/components/common/loading-spinner";
import { handleTabArrowKeyDown } from "@/components/common/tab-keyboard-nav";
import {
  adjustAdvertiserCapabilityGrant,
  issueAdvertiserRecoveryLink,
  listAdvertiserGrants,
  listAuthorizedPublishers,
  revokeAdvertiserGrant,
  unlockAdvertiserContentRevision,
  type AdvertiserGrant,
} from "@/lib/admin-api";
import {
  advertiserLabel,
  advertiserNeedsAttention,
  advertiserStatusLabel,
  canSubmitAdjust,
  emptyAdjustForm,
  formatAdvertiserSummary,
  formatExpiry,
  formatQuota,
  groupAdvertisers,
  MIN_REASON_LENGTH,
  parseAdjustQuotaLimit,
  quotaBelowUsed,
  quotaFraction,
  resolveWindowToInstant,
  statusLabel,
  WINDOW_PRESET_DAYS,
  windowPresetLabel,
  type AdjustFormState,
  type AdvertiserRow,
  type AdvertiserStatus,
} from "@/lib/advertisers";

import { AdvertiserWizard } from "./advertiser-wizard";
import { CredentialHandover } from "./credential-handover";
import { PrincipalSearch, type PrincipalOption } from "./principal-search";
import styles from "./advertisers-page.module.css";

/**
 * The advertisers page: a LIST of advertisers, and one wizard that adds
 * another.
 *
 * # WHY THIS PAGE IS SHAPED LIKE THIS
 *
 * It used to be a stack of five independent cards — create an account, issue a
 * grant, make a squad, list the grants, unlock a revision — each of which did
 * one call and left the operator to remember the order and to notice when a
 * step had not happened. The verdict on it was that it was disjointed, and the
 * verdict was right: nothing on the page ever answered "who are my
 * advertisers and what state are they in", which is the only question an
 * operator arrives with.
 *
 * So the landing view is the answer to that question, one row per advertiser
 * principal, and everything that used to be a card is either a row action or a
 * step inside the wizard.
 *
 * # TWO TABS, NOT A STACK
 *
 * The list and the wizard used to render on top of one another on this same
 * view — a second verdict, this time on IA rather than on the data model: an
 * operator who arrives to find an account (the common case) had to scroll past
 * a provisioning form to get there. They are now the two tabs of this page,
 * same `?tab=` query-param pattern as Users & Access's own Scholarships tab
 * (users-list.tsx) — "All" is the default/landing tab, and "New" is reachable
 * both by clicking the tab and by a `/advertisers?tab=new` deep link. The
 * `handover` recovery-link callout stays on the list's branch, not hoisted
 * above the tabs, because only a list row action (Recovery link) ever sets it.
 *
 * # WHAT AN ADVERTISER IS
 *
 * A plain FREE account plus an expiring capability grant. Their handle and
 * display name ARE the brand: there is no squad and no separate publisher
 * record, and their publishes attribute to them automatically
 * (content-service#205). Nothing on the account marks it as an advertiser —
 * the permission is the grant, and when it lapses the account is ordinary
 * again.
 *
 * # WHERE THE ROWS COME FROM
 *
 * ListPrincipalCapabilityGrants with revoked and expired INCLUDED — always,
 * not behind a toggle. A lapsed advertiser is exactly the one an operator
 * cannot find, and hiding them by default is what made the account
 * "disappear". The grants are then grouped by principal and enriched with:
 *
 *   - handle/display name, resolved server-side from user-service;
 *   - whether that resolution actually ran (identitiesResolved), which is what
 *     keeps a failed lookup from being read as anything about the accounts
 *     themselves — see deriveAdvertiserStatus;
 *   - the sponsored-links disclosure flag, joined from the authorized-publisher
 *     allowlist. Best-effort: a failure there leaves the fact unknown and says
 *     so, and never blocks the list.
 *
 * # SECRETS
 *
 * A recovery link is held in component state only, rendered by
 * CredentialHandover, and dropped when dismissed. It is never cached, stored,
 * or put in a query key.
 */

interface HandoverState {
  secret: string;
  accountLabel: string;
  passwordChangeEnforced: boolean;
}

const TABS = ["All", "New"] as const;
type Tab = (typeof TABS)[number];

// The ?tab= search param is what makes the wizard deep-linkable: reload or
// share /advertisers?tab=new and it lands straight there. Anything else,
// including no param at all, lands on the list — that is where an operator
// finds an account, so it stays the default.
function tabFromSearchParams(params: URLSearchParams): Tab {
  return params.get("tab") === "new" ? "New" : "All";
}

/** ROW_ACTION_TARGET pairs an action with the exact grant it will act on. A
 * principal can hold several grants, and an action that silently picked one of
 * them would eventually pick the wrong one. */
interface GrantTarget {
  row: AdvertiserRow<AdvertiserGrant>;
  grant: AdvertiserGrant;
}

export function AdvertisersPage() {
  const toast = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = tabFromSearchParams(searchParams);

  function handleTabChange(tab: Tab) {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (tab === "New") {
          next.set("tab", "new");
        } else {
          next.delete("tab");
        }
        return next;
      },
      { replace: false },
    );
  }

  const [grants, setGrants] = useState<AdvertiserGrant[]>([]);
  const [identitiesResolved, setIdentitiesResolved] = useState(false);
  const [sponsoredLinkIds, setSponsoredLinkIds] = useState<ReadonlySet<string> | null>(null);
  const [loading, setLoading] = useState(false);
  const [listError, setListError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [hideFinished, setHideFinished] = useState(false);
  const [filterPrincipal, setFilterPrincipal] = useState<PrincipalOption | null>(null);

  const [expanded, setExpanded] = useState<string | null>(null);

  const [revokeTarget, setRevokeTarget] = useState<GrantTarget | null>(null);
  const [revokeReason, setRevokeReason] = useState("");
  const [revoking, setRevoking] = useState(false);

  const [adjustTarget, setAdjustTarget] = useState<GrantTarget | null>(null);
  const [adjustForm, setAdjustForm] = useState<AdjustFormState>(emptyAdjustForm);
  const [adjustCustomDateInput, setAdjustCustomDateInput] = useState("");
  const [adjusting, setAdjusting] = useState(false);
  const [adjustError, setAdjustError] = useState<string | null>(null);

  const [unlockTarget, setUnlockTarget] = useState<AdvertiserRow<AdvertiserGrant> | null>(null);

  const [recoveryTarget, setRecoveryTarget] = useState<AdvertiserRow<AdvertiserGrant> | null>(null);
  const [recoveryReason, setRecoveryReason] = useState("");
  const [issuingRecovery, setIssuingRecovery] = useState(false);

  const [handover, setHandover] = useState<HandoverState | null>(null);

  const reload = useCallback(() => setReloadKey((value) => value + 1), []);

  useEffect(() => {
    // Lazy-load: skip the fetch entirely while the New tab is active, e.g. a
    // deep link straight to /advertisers?tab=new never needs the list at all.
    if (activeTab !== "All") return;

    let cancelled = false;
    setLoading(true);
    setListError(null);

    // The allowlist read is deliberately not awaited as a hard dependency: it
    // enriches the rows, it does not define them. Its failure is folded into
    // `null` (meaning "unknown"), never into the list's error state.
    const publishers = listAuthorizedPublishers().then(
      (data) =>
        new Set(
          (data.publishers ?? [])
            .filter((publisher) => publisher.sponsoredLinks === true)
            .map((publisher) => publisher.publisherId),
        ) as ReadonlySet<string>,
      () => null,
    );

    Promise.all([
      listAdvertiserGrants({
        principalId: filterPrincipal?.id,
        // Always both. See "WHERE THE ROWS COME FROM": a lapsed advertiser is
        // precisely the one somebody is looking for.
        includeRevoked: true,
        includeExpired: true,
      }),
      publishers,
    ])
      .then(([data, flagged]) => {
        if (cancelled) return;
        setGrants(data.grants ?? []);
        setIdentitiesResolved(data.identitiesResolved === true);
        setSponsoredLinkIds(flagged);
      })
      .catch((err: Error) => {
        if (cancelled) return;
        setListError(err.message ?? "Failed to load advertisers");
        setGrants([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [activeTab, reloadKey, filterPrincipal]);

  const rows = useMemo(
    () =>
      groupAdvertisers(grants, {
        identitiesResolved,
        sponsoredLinkPublisherIds: sponsoredLinkIds,
      }),
    [grants, identitiesResolved, sponsoredLinkIds],
  );

  const visibleRows = hideFinished
    ? rows.filter((row) => row.status !== "expired" && row.status !== "revoked")
    : rows;

  async function handleRevoke() {
    if (!revokeTarget || revokeReason.trim().length < MIN_REASON_LENGTH) return;
    setRevoking(true);
    try {
      await revokeAdvertiserGrant(revokeTarget.grant.id, revokeReason.trim());
      toast.success("The grant has been revoked.", "Revoked");
      setRevokeTarget(null);
      setRevokeReason("");
      reload();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to revoke", "Revoke failed");
    } finally {
      setRevoking(false);
    }
  }

  function updateAdjustForm(patch: Partial<AdjustFormState>) {
    setAdjustForm((current) => ({ ...current, ...patch }));
  }

  async function handleAdjust() {
    if (!adjustTarget || !canSubmitAdjust(adjustForm, adjustTarget.grant)) return;

    const quotaLimit = parseAdjustQuotaLimit(adjustForm);
    // null is "the operator typed something that is not a quota". canSubmitAdjust
    // above already refuses that, so this is unreachable -- it narrows the type
    // to what the API takes (a number, or undefined for "leave it alone").
    if (quotaLimit === null) return;
    const expiresAt = adjustForm.choice
      ? resolveWindowToInstant(adjustForm.choice).toISOString()
      : undefined;

    setAdjusting(true);
    setAdjustError(null);
    try {
      const { grant } = await adjustAdvertiserCapabilityGrant(adjustTarget.grant.id, {
        quotaLimit,
        expiresAt,
        reason: adjustForm.reason.trim(),
      });
      toast.success(`Now ${formatQuota(grant)}. ${formatExpiry(grant)}.`, "Grant adjusted");
      setAdjustTarget(null);
      setAdjustForm(emptyAdjustForm);
      setAdjustCustomDateInput("");
      reload();
    } catch (err) {
      setAdjustError(err instanceof Error ? err.message : "Failed to adjust the grant");
    } finally {
      setAdjusting(false);
    }
  }

  async function handleIssueRecovery() {
    if (!recoveryTarget || recoveryReason.trim().length < MIN_REASON_LENGTH) return;
    setIssuingRecovery(true);
    try {
      const result = await issueAdvertiserRecoveryLink(
        recoveryTarget.principalId,
        recoveryReason.trim(),
      );
      if (result.recoveryLink) {
        setHandover({
          secret: result.recoveryLink,
          accountLabel: advertiserLabel(recoveryTarget),
          passwordChangeEnforced: result.passwordChangeEnforced,
        });
      } else {
        // auth-service treats a failure to mint the link as non-fatal because
        // the sessions are already revoked by that point. Say exactly that
        // rather than reporting an error that invites a retry — a retry would
        // revoke sessions again to no benefit.
        toast.error(
          `${result.sessionsRevoked} session(s) were signed out, but no recovery link could be created. Try again to get a link.`,
          "Signed out, no link",
        );
      }
      setRecoveryTarget(null);
      setRecoveryReason("");
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Failed to issue a recovery link",
        "Recovery link failed",
      );
    } finally {
      setIssuingRecovery(false);
    }
  }

  return (
    <div className={styles.page}>
      <div className={styles.titleRow}>
        <h1 className={styles.title}>Advertisers</h1>
      </div>

      <ProfileTabs
        role="tablist"
        aria-label="Advertisers"
        tabs={TABS.map((tab) => ({ id: tab, label: tab }))}
        activeTab={activeTab}
        onKeyDown={handleTabArrowKeyDown}
        onTabChange={(tab) => handleTabChange(tab as Tab)}
      />

      {activeTab === "New" ? (
        <AdvertiserWizard onClose={() => handleTabChange("All")} onProvisioned={reload} />
      ) : (
        <>
          {handover ? (
            <CredentialHandover
              kind="recovery-link"
              secret={handover.secret}
              accountLabel={handover.accountLabel}
              passwordChangeEnforced={handover.passwordChangeEnforced}
              onDismiss={() => setHandover(null)}
            />
          ) : null}

          <section className={styles.card}>
            <div className={styles.listHeader}>
              <h2 className={styles.cardTitle}>All advertisers</h2>
            </div>

            <div className={styles.filters}>
              <PrincipalSearch
                id="advertiser-filter-principal"
                label="Filter by account"
                selected={filterPrincipal}
                onSelect={setFilterPrincipal}
                placeholder="All"
              />
              <label className={styles.checkboxLabel}>
                <input
                  type="checkbox"
                  checked={hideFinished}
                  onChange={(event) => setHideFinished(event.currentTarget.checked)}
                />
                <span>Hide finished</span>
              </label>
            </div>

            {!loading && !listError && sponsoredLinkIds === null ? (
              <p className={styles.fieldHint}>
                Sponsored-link disclosure could not be read, so it is not shown per row.
              </p>
            ) : null}

            {loading ? <LoadingSpinner size={20} label="Loading advertisers..." /> : null}

            {!loading && listError ? (
              <div className={styles.errorState}>
                <p className={styles.errorTitle}>Can&apos;t load advertisers</p>
                <p className={styles.errorText}>{listError}</p>
              </div>
            ) : null}

            {!loading && !listError && visibleRows.length === 0 ? (
              <p className={styles.emptyState}>No advertisers yet.</p>
            ) : null}

            {!loading && !listError && visibleRows.length > 0 ? (
              <DataTable
                className={styles.table}
                columns="minmax(200px, 1.4fr) minmax(170px, 1fr) minmax(150px, 0.85fr) minmax(140px, 0.65fr) minmax(210px, 240px)"
                header={["Advertiser", "Status", "Allowance", "Ends", "Actions"]}
              >
                {visibleRows.map((row) => (
                  <AdvertiserRowView
                    key={row.principalId}
                    row={row}
                    expanded={expanded === row.principalId}
                    onToggleExpanded={() =>
                      setExpanded((current) =>
                        current === row.principalId ? null : row.principalId,
                      )
                    }
                    onAdjust={(grant) => {
                      setAdjustTarget({ row, grant });
                      setAdjustForm(emptyAdjustForm);
                      setAdjustCustomDateInput("");
                      setAdjustError(null);
                    }}
                    onRevoke={(grant) => {
                      setRevokeTarget({ row, grant });
                      setRevokeReason("");
                    }}
                    onUnlock={() => setUnlockTarget(row)}
                    onIssueRecovery={() => {
                      setRecoveryTarget(row);
                      setRecoveryReason("");
                    }}
                  />
                ))}
              </DataTable>
            ) : null}
          </section>
        </>
      )}

      {revokeTarget ? (
        <ReasonPrompt
          title="Revoke this grant"
          description="The account stops being able to publish advertiser content immediately. Anything it already published stays published."
          confirmLabel={revoking ? "Revoking…" : "Revoke grant"}
          reason={revokeReason}
          onReasonChange={setRevokeReason}
          busy={revoking}
          onCancel={() => setRevokeTarget(null)}
          onConfirm={() => void handleRevoke()}
        />
      ) : null}

      {recoveryTarget ? (
        <ReasonPrompt
          title="Issue a recovery link"
          description="Signs the account out everywhere and creates a one-time link to set a new password."
          confirmLabel={issuingRecovery ? "Issuing…" : "Issue link"}
          reason={recoveryReason}
          onReasonChange={setRecoveryReason}
          busy={issuingRecovery}
          onCancel={() => setRecoveryTarget(null)}
          onConfirm={() => void handleIssueRecovery()}
        />
      ) : null}

      {unlockTarget ? (
        <UnlockRevisionPrompt
          row={unlockTarget}
          onCancel={() => setUnlockTarget(null)}
          onDone={(contentId) => {
            toast.success(`One more revision is now allowed on ${contentId}.`, "Revision unlocked");
            setUnlockTarget(null);
          }}
        />
      ) : null}

      {adjustTarget ? (
        <AdjustGrantPrompt
          grant={adjustTarget.grant}
          form={adjustForm}
          onFormChange={updateAdjustForm}
          customDateInput={adjustCustomDateInput}
          onCustomDateInputChange={setAdjustCustomDateInput}
          busy={adjusting}
          error={adjustError}
          onCancel={() => {
            setAdjustTarget(null);
            setAdjustForm(emptyAdjustForm);
            setAdjustCustomDateInput("");
            setAdjustError(null);
          }}
          onConfirm={() => void handleAdjust()}
        />
      ) : null}
    </div>
  );
}

/** statusClassName maps a derived status onto the badge styles. Hyphenated
 * status values do not survive a CSS class name, so the mapping is explicit
 * rather than interpolated — an interpolated one silently falls through to the
 * default for exactly the state that most needs to stand out. */
function statusClassName(status: AdvertiserStatus): string {
  switch (status) {
    case "expiring-soon":
      return styles.status_expiring;
    case "active":
      return styles.status_active;
    case "scheduled":
      return styles.status_scheduled;
    case "exhausted":
      return styles.status_exhausted;
    case "expired":
      return styles.status_expired;
    case "revoked":
      return styles.status_revoked;
    default:
      return styles.statusDefault;
  }
}

function AdvertiserRowView({
  row,
  expanded,
  onToggleExpanded,
  onAdjust,
  onRevoke,
  onUnlock,
  onIssueRecovery,
}: {
  row: AdvertiserRow<AdvertiserGrant>;
  expanded: boolean;
  onToggleExpanded: () => void;
  onAdjust: (grant: AdvertiserGrant) => void;
  onRevoke: (grant: AdvertiserGrant) => void;
  onUnlock: () => void;
  onIssueRecovery: () => void;
}) {
  const governing = row.governing;
  const fraction = quotaFraction(governing);
  const label = advertiserLabel(row);
  const live = governing.status === "active" || governing.status === "scheduled";
  // AdjustPrincipalCapabilityGrant rejects a revoked grant (FailedPrecondition)
  // — adjusting is a remedy for a grant that is still in force, or merely
  // expired/exhausted, not one that is over.
  const canAdjust = governing.status !== "revoked";
  const older = row.grants.slice(1);

  return (
    <DataRow
      className={[styles.tableRow, advertiserNeedsAttention(row) ? styles.rowAttention : null]
        .filter(Boolean)
        .join(" ")}
    >
      <div className={styles.identityCell} title={row.principalId}>
        <span className={styles.identityName}>{label}</span>
        {row.handle ? <span className={styles.identityId}>@{row.handle}</span> : null}
        <span className={styles.identityId}>{row.principalId}</span>
        {row.sponsoredLinks === false ? (
          <span className={styles.flagWarning}>No sponsored-link disclosure</span>
        ) : null}
        {older.length > 0 ? (
          <button type="button" className={styles.rowButton} onClick={onToggleExpanded}>
            {expanded ? "Hide" : `${older.length} earlier grant${older.length === 1 ? "" : "s"}`}
          </button>
        ) : null}
        {expanded ? (
          <div className={styles.grantHistory}>
            {older.map((grant) => (
              <div key={grant.id} className={styles.grantHistoryRow}>
                <span className={statusClassName(grant.status)}>{statusLabel(grant.status)}</span>
                <span>{formatQuota(grant)}</span>
                <span>{formatExpiry(grant)}</span>
                {grant.status !== "revoked" ? (
                  <button
                    type="button"
                    className={styles.rowButton}
                    onClick={() => onAdjust(grant)}
                  >
                    Adjust
                  </button>
                ) : null}
                {grant.status === "active" || grant.status === "scheduled" ? (
                  <button
                    type="button"
                    className={styles.rowButtonDanger}
                    onClick={() => onRevoke(grant)}
                  >
                    Revoke
                  </button>
                ) : null}
              </div>
            ))}
          </div>
        ) : null}
      </div>

      <div className={styles.summaryCell} data-label="Status">
        <span className={statusClassName(row.status)}>{advertiserStatusLabel(row.status)}</span>
        <span>{formatAdvertiserSummary(row)}</span>
      </div>

      <div className={styles.quotaCell} data-label="Allowance">
        <span>{formatQuota(governing)}</span>
        {fraction !== null ? (
          <span className={styles.meterTrack} aria-hidden>
            <span className={styles.meterFill} style={{ width: `${fraction * 100}%` }} />
          </span>
        ) : null}
      </div>

      <span data-label="Ends">{formatExpiry(governing)}</span>

      <div className={styles.actionsCell}>
        {canAdjust ? (
          <button type="button" className={styles.rowButton} onClick={() => onAdjust(governing)}>
            Adjust
          </button>
        ) : null}
        {live ? (
          <button
            type="button"
            className={styles.rowButtonDanger}
            onClick={() => onRevoke(governing)}
          >
            Revoke
          </button>
        ) : null}
        <button type="button" className={styles.rowButton} onClick={onUnlock}>
          Unlock a revision
        </button>
        <button type="button" className={styles.rowButton} onClick={onIssueRecovery}>
          Recovery link
        </button>
      </div>
    </DataRow>
  );
}

/**
 * ReasonPrompt is the confirm step for the actions that need one. They all
 * REQUIRE a reason — admin-service rejects a blank one, and this is the audit
 * trail for a commercial permission, so it is collected here rather than
 * discovered as a 400.
 */
function ReasonPrompt({
  title,
  description,
  confirmLabel,
  reason,
  onReasonChange,
  busy,
  onCancel,
  onConfirm,
}: {
  title: string;
  description: string;
  confirmLabel: string;
  reason: string;
  onReasonChange: (value: string) => void;
  busy: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  const [reasonTouched, setReasonTouched] = useState(false);
  const valid = reason.trim().length >= MIN_REASON_LENGTH;

  return (
    <div className={styles.promptOverlay} role="dialog" aria-modal="true" aria-label={title}>
      <div className={styles.prompt}>
        <h3 className={styles.promptTitle}>{title}</h3>
        <p className={styles.cardHint}>{description}</p>
        <label className={styles.field}>
          <span className={styles.fieldLabel}>Reason</span>
          <textarea
            className={styles.textarea}
            rows={2}
            value={reason}
            onChange={(event) => onReasonChange(event.currentTarget.value)}
            onBlur={() => setReasonTouched(true)}
          />
          {reasonTouched && !valid ? (
            <span className={styles.errorText}>Reason must be at least 3 characters.</span>
          ) : null}
        </label>
        <div className={styles.promptActions}>
          <button
            type="button"
            className={styles.secondaryButton}
            onClick={onCancel}
            disabled={busy}
          >
            Cancel
          </button>
          <button
            type="button"
            className={styles.primaryButton}
            onClick={onConfirm}
            disabled={!valid || busy}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * UnlockRevisionPrompt admits exactly one more edit to a locked, published
 * revision.
 *
 * The content id is typed here rather than picked, because this panel has no
 * content listing to hang the action off — but it is a ROW action rather than
 * a page-level form, so the operator has already said which advertiser they
 * are remediating and the dialog can say so back to them.
 */
function UnlockRevisionPrompt({
  row,
  onCancel,
  onDone,
}: {
  row: AdvertiserRow<AdvertiserGrant>;
  onCancel: () => void;
  onDone: (contentId: string) => void;
}) {
  const [contentId, setContentId] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const valid = contentId.trim().length > 0 && reason.trim().length >= MIN_REASON_LENGTH;

  async function submit() {
    if (!valid) return;
    const id = contentId.trim();
    setBusy(true);
    setError(null);
    try {
      await unlockAdvertiserContentRevision(id, reason.trim());
      onDone(id);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to unlock a revision");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      className={styles.promptOverlay}
      role="dialog"
      aria-modal="true"
      aria-label="Unlock a revision"
    >
      <div className={styles.prompt}>
        <h3 className={styles.promptTitle}>Unlock a revision</h3>
        <p className={styles.cardHint}>
          Allows exactly one more edit to a locked, published revision by {advertiserLabel(row)}.
        </p>

        <label className={styles.field}>
          <span className={styles.fieldLabel}>Content ID</span>
          <input
            className={styles.input}
            type="text"
            value={contentId}
            placeholder="content id"
            onChange={(event) => {
              setContentId(event.currentTarget.value);
              if (error) setError(null);
            }}
          />
        </label>

        <label className={styles.field}>
          <span className={styles.fieldLabel}>Reason</span>
          <textarea
            className={styles.textarea}
            rows={2}
            value={reason}
            placeholder="Why does this need another revision?"
            onChange={(event) => setReason(event.currentTarget.value)}
          />
        </label>

        {error ? (
          <p className={styles.errorText} role="alert">
            {error}
          </p>
        ) : null}

        <div className={styles.promptActions}>
          <button
            type="button"
            className={styles.secondaryButton}
            onClick={onCancel}
            disabled={busy}
          >
            Cancel
          </button>
          <button
            type="button"
            className={styles.primaryButton}
            onClick={() => void submit()}
            disabled={!valid || busy}
          >
            {busy ? "Unlocking…" : "Unlock one revision"}
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * AdjustGrantPrompt is the "Adjust quota / expiry" dialog: a raised (or set)
 * quota limit and/or a moved expiry, both optional and independent, plus the
 * mandatory reason every mutation on this panel requires.
 *
 * Every control here has a "leave this alone" state (blank quota input, no
 * chip selected), because this is a partial update, not a from-scratch grant.
 * Raising or lowering the limit never touches quota_used — usage is tracked
 * independently server-side.
 */
function AdjustGrantPrompt({
  grant,
  form,
  onFormChange,
  customDateInput,
  onCustomDateInputChange,
  busy,
  error,
  onCancel,
  onConfirm,
}: {
  grant: AdvertiserGrant;
  form: AdjustFormState;
  onFormChange: (patch: Partial<AdjustFormState>) => void;
  customDateInput: string;
  onCustomDateInputChange: (value: string) => void;
  busy: boolean;
  error: string | null;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  const quotaLimit = parseAdjustQuotaLimit(form);
  const belowUsed = quotaBelowUsed(quotaLimit, grant);
  const canSubmit = canSubmitAdjust(form, grant) && !busy;
  const [reasonTouched, setReasonTouched] = useState(false);
  const reasonInvalid = reasonTouched && form.reason.trim().length < MIN_REASON_LENGTH;

  return (
    <div
      className={styles.promptOverlay}
      role="dialog"
      aria-modal="true"
      aria-label="Adjust quota or expiry"
    >
      <div className={styles.prompt}>
        <h3 className={styles.promptTitle}>Adjust quota or expiry</h3>
        <p className={styles.cardHint}>
          Raise the post allowance, move the expiry, or both. Leave a control alone to keep its
          current value.
        </p>

        <div className={styles.field}>
          <span className={styles.fieldLabel}>New post allowance (optional)</span>
          <div className={styles.quotaRow}>
            <input
              className={styles.quotaInput}
              type="number"
              min={0}
              inputMode="numeric"
              value={form.quotaInput}
              disabled={form.unmetered}
              placeholder={`Currently ${grant.isUnmetered ? "unmetered" : grant.quotaLimit}`}
              aria-label="New post allowance"
              onChange={(event) => onFormChange({ quotaInput: event.currentTarget.value })}
            />
            <label className={styles.checkboxLabel}>
              <input
                type="checkbox"
                checked={form.unmetered}
                onChange={(event) => onFormChange({ unmetered: event.currentTarget.checked })}
              />
              <span>Make unmetered</span>
            </label>
          </div>
          {belowUsed ? (
            <p className={styles.errorText} role="alert">
              {grant.quotaUsed} already used. The new limit must be at least that many.
            </p>
          ) : (
            <p className={styles.fieldHint}>
              {grant.quotaUsed} already used. Leave blank to keep the current limit.
            </p>
          )}
        </div>

        <div className={styles.field}>
          <span className={styles.fieldLabel}>New expiry (optional)</span>
          <div className={styles.chipRow}>
            {WINDOW_PRESET_DAYS.map((days) => (
              <button
                key={days}
                type="button"
                className={
                  form.choice?.kind === "preset" && form.choice.days === days
                    ? styles.chipActive
                    : styles.chip
                }
                onClick={() => onFormChange({ choice: { kind: "preset", days } })}
              >
                {windowPresetLabel(days)} from now
              </button>
            ))}
            <button
              type="button"
              className={form.choice?.kind === "custom" ? styles.chipActive : styles.chip}
              onClick={() =>
                onFormChange({
                  choice: {
                    kind: "custom",
                    date: customDateInput
                      ? new Date(`${customDateInput}T00:00:00.000Z`)
                      : new Date(Number.NaN),
                  },
                })
              }
            >
              Custom date
            </button>
          </div>
          {form.choice?.kind === "custom" ? (
            // No `min` here, unlike the wizard's date input: shortening a
            // window into the past is a legitimate way to end a grant early
            // (see canSubmitAdjust / AdjustPrincipalCapabilityGrant's proto
            // doc), not a mistake to block.
            <input
              className={styles.input}
              type="date"
              aria-label="New expiry date"
              value={customDateInput}
              onChange={(event) => {
                const value = event.currentTarget.value;
                onCustomDateInputChange(value);
                onFormChange({
                  choice: {
                    kind: "custom",
                    date: value ? new Date(`${value}T00:00:00.000Z`) : new Date(Number.NaN),
                  },
                });
              }}
            />
          ) : null}
          <p className={styles.fieldHint}>Leave unset to keep the current expiry.</p>
        </div>

        <label className={styles.field}>
          <span className={styles.fieldLabel}>Reason</span>
          <textarea
            className={styles.textarea}
            rows={2}
            value={form.reason}
            placeholder="Why is this being adjusted?"
            onChange={(event) => onFormChange({ reason: event.currentTarget.value })}
            onBlur={() => setReasonTouched(true)}
          />
          {reasonInvalid ? (
            <span className={styles.errorText}>Reason must be at least 3 characters.</span>
          ) : null}
        </label>

        {error ? (
          <p className={styles.errorText} role="alert">
            {error}
          </p>
        ) : null}

        <div className={styles.promptActions}>
          <button
            type="button"
            className={styles.secondaryButton}
            onClick={onCancel}
            disabled={busy}
          >
            Cancel
          </button>
          <button
            type="button"
            className={styles.primaryButton}
            onClick={onConfirm}
            disabled={!canSubmit}
          >
            {busy ? "Adjusting…" : "Adjust grant"}
          </button>
        </div>
      </div>
    </div>
  );
}
